<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, watch, onMounted, ref } from 'vue'
import { useLogger } from 'vue-logger-plugin'
import { TextBox } from '@progress/kendo-vue-inputs'
import type { IAoeNewUserWizard } from '../../types/WizardTypes'
import { BusinessNameUpdate } from '../../api/Business.ts'
import { GetBeeNumberAuthenicatedUser, GetCountryCode, GetPwsBaseUrl } from '../../stores/SettingsStore.ts'
import { getCleanString } from '../../utils/businessNameHelper.ts'

const { t } = useI18n()
const logger = useLogger()

// Props defined explicitly to avoid TypeScript errors
const props = defineProps({
  formData: {
    type: Object,
    required: true
  },
  onUpdate: {
    type: Function,
    required: true
  },
  isValid: {
    type: Boolean,
    required: true
  }
})

// Emit events to parent component
const emit = defineEmits(['update:isValid', 'skip-french-contribution', 'next-step', 'skip-to-step'])

// State for handling business name update feedback
const isLoading = ref(false)
const businessNameUpdateSuccess = ref(false)
const businessNameUpdateError = ref('')
const isBusinessNameUpdating = ref(false)
const personalUrl = ref('')
const countryCode = ref('')

// Check if business name is valid for update (non-empty and ASCII only)
const isBusinessNameValid = computed(() => {
  const trimmed = props.formData.businessName.trim()
  return trimmed !== '' && /^[\x00-\x7F]+$/.test(trimmed)
})

// Track if business name has been successfully updated
const isBusinessNameUpdated = ref(false)

// Check if the step is valid
const isStepValid = computed(() => {
  return isBusinessNameUpdated.value
})

// Calculate validation message to show in tooltip
const validationMessage = computed(() => {
  if (!isBusinessNameUpdated.value) {
    return t('validators.businessNameRequired')
  }
  return ''
})

// Update step validity when it changes
watch(isStepValid, (newValue) => {
  emit('update:isValid', newValue)
})

// Update business name
async function updateBusinessName() {
  try {
    // Reset status
    businessNameUpdateSuccess.value = false
    businessNameUpdateError.value = ''
    isBusinessNameUpdating.value = true

    // Get the authenticated user's BEE number
    const beeNumber = await GetBeeNumberAuthenicatedUser()

    if (!beeNumber) {
      businessNameUpdateError.value = t('errors.updatingBusinessName')
      logger.error('Error updating business name: No BEE number available')
      return false
    }

    const trimmedBusinessName = props.formData.businessName.trim()
    if (/[^\x00-\x7F]/.test(trimmedBusinessName)) {
      businessNameUpdateError.value = t('consultantCorner.companyName.BusinessNameInvalidCharacters')
      isBusinessNameUpdated.value = false
      return false
    }

    const cleanedBusinessName = getCleanString(props.formData.businessName)
    if (cleanedBusinessName !== props.formData.businessName) {
      props.onUpdate({ businessName: cleanedBusinessName })
    }

    const response = await BusinessNameUpdate(beeNumber, cleanedBusinessName)

    console.log("Business name update response:", response)
    // Check if response exists and if there's an error code for business name already exists
    if (response && response.responseStatus && response.responseStatus.errorCode === 'BusinessNameAlreadyExists') {
      businessNameUpdateError.value = t('errors.businessNameAlreadyExists')
      console.log("Business name already exists:", response)
      logger.error('Business name already exists:', response)
      isBusinessNameUpdated.value = false
      return false
    }

    // Check if response exists and if ErrorCode and Message are empty or null (indicating success)
    if (response == null || response.responseStatus == null) {
      businessNameUpdateSuccess.value = true
      isBusinessNameUpdated.value = true
      logger.info('Business name updated successfully')
      return true
    } else {
      businessNameUpdateError.value = t('errors.updatingBusinessName')
      logger.error('Error updating business name:', response)
      isBusinessNameUpdated.value = false
      return false
    }
  } catch (error) {
    businessNameUpdateError.value = t('errors.updatingBusinessName')
    logger.error('Error updating business name:', error)
    isBusinessNameUpdated.value = false
    return false
  } finally {
    isBusinessNameUpdating.value = false
  }
}

// Function to check if business name is already set
async function checkBusinessNameExists() {
  // Don't make API calls that would cause automatic redirection
  // Just get the country code for reference
  try {
    isLoading.value = true
    countryCode.value = await GetCountryCode() || ''

    // Don't emit any skip-to-step events here to prevent automatic navigation
    logger.debug('BusinessStep initialized with country code:', countryCode.value)
  } catch (error) {
    logger.error('Error getting country code:', error)
  } finally {
    isLoading.value = false
  }
}

// Function to update personal URL based on business name
async function updatePersonalUrl(businessName: string) {
  if (businessName && businessName.trim() !== '') {
    const businessNameForUrl = businessName.trim().toLowerCase().replace(/\s+/g, '-')
    const baseUrl = await GetPwsBaseUrl()
    personalUrl.value = `${baseUrl}${businessNameForUrl}`
  } else {
    personalUrl.value = ''
  }
}

// Watch for changes in business name to update personal URL
watch(() => props.formData.businessName, (newValue) => {
  businessNameUpdateError.value = ''

  if (/[^\x00-\x7F]/.test(newValue.trim())) {
    businessNameUpdateError.value = t('consultantCorner.companyName.BusinessNameInvalidCharacters')
    return
  }

  const cleanedBusinessName = getCleanString(newValue)

  if (cleanedBusinessName !== newValue) {
    props.onUpdate({ businessName: cleanedBusinessName })
    return
  }

  updatePersonalUrl(cleanedBusinessName)
}, { immediate: true })

// Load country code when mounting the component
onMounted(async () => {
  // Only get country code if we don't already have it
  if (!countryCode.value) {
    try {
      countryCode.value = await GetCountryCode()
    } catch (error) {
      logger.error('Error getting country code:', error)
      // Default to empty string if there's an error
      countryCode.value = ''
    }
  }

  // Skip checking business name existence to prevent unwanted API calls
  // Just validate the current state based on existing data

  // Validate step when component loads
  if (isBusinessNameUpdated.value) {
    emit('update:isValid', true)
  }
})
</script>

<template>
  <div class="wizard-step">
    <div class="wizard-step-header">
      <h3>{{ t('labels.BusinessInfoHeader') }}</h3>
      <p>{{ t('labels.BusinessInfoSubHeader') }}</p>
    </div>

    <div class="form-section">
      <!-- Business Name Row with label and input aligned horizontally -->
      <div class="business-name-row">
        <div class="business-name-label">
          <label class="form-label">{{ t('labels.BusinessName') }} *</label>
        </div>
        <div class="business-name-input">
          <TextBox
            v-model="formData.businessName"
            class="k-textbox"
            :placeholder="t('labels.BusinessNamePlaceholder')"
            data-testid="businessName"
          />
        </div>
        <div class="business-name-update-button">
          <button
            class="btn primaryBtn btn-sm"
            @click="updateBusinessName"
            :disabled="!isBusinessNameValid || isBusinessNameUpdating"
          >
           {{ t('actions.UpdateBusinessName') }}
          </button>
        </div>
      </div>

      <!-- Alerts appear below the input row -->
      <div class="business-name-alerts">
        <!-- Success alert for business name update -->
        <div v-if="businessNameUpdateSuccess" class="alert alert-success alert-dismissible fade show" role="alert">
          <strong>{{ t('labels.Success') }}</strong> {{ t('messages.businessNameUpdated') }}
          <button type="button" class="btn-close" @click="businessNameUpdateSuccess = false" :aria-label="t('buttons.Close')"></button>
        </div>

        <!-- Error alert for business name update failure -->
        <div v-if="businessNameUpdateError" class="alert alert-danger alert-dismissible fade show" role="alert">
          <strong>{{ t('labels.Error') }}</strong> {{ businessNameUpdateError }}
          <button type="button" class="btn-close" @click="businessNameUpdateError = ''" :aria-label="t('buttons.Close')"></button>
        </div>
      </div>

      <!-- Personal URL display appears below alerts -->
      <div v-if="personalUrl" class="personal-url-row">
        <label class="form-label">{{ t('labels.PersonalURL') }}</label>
        <div class="personal-url-display">
          {{ personalUrl }}
        </div>
      </div>

      <!-- Validation message for business step -->
      <div v-if="!isStepValid" class="step-validation-message">
        {{ t('validators.stepRequirementsHeader') }}
        <ul>
          <li v-if="!isBusinessNameUpdated" class="validation-item">
            {{ t('validators.businessNameRequired') }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.wizard-step {
  margin-bottom: 25px;
}

.wizard-step-header {
  margin-bottom: 16px;
}

.wizard-step-header h3 {
  @include typography-h4;
  @include font-weight-semibold;
  margin-bottom: 6px;
}

.wizard-step-header p {
  @include typography-body;
  color: #666;
  margin-bottom: 10px;
}

.form-section {
  margin-bottom: 25px;
}

.form-row {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  display: block;
  margin-bottom: 6px;
  width: 100%;
  word-wrap: break-word;
  white-space: normal;
}

/* Business name section layout */
.business-name-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  min-height: 34px;
  flex-wrap: wrap;
}

.business-name-label {
  width: 120px;
  flex-shrink: 0;
}

.business-name-label .form-label {
  margin-bottom: 0;
  line-height: 34px;
  height: 34px;
  display: flex;
  align-items: center;
}

.business-name-input {
  flex: 1;
  margin-right: 15px;
  min-width: 200px;
}

.business-name-update-button {
  width: auto;
  flex-shrink: 0;
}

.business-name-update-button .btn {
  @include typography-body;
  height: 34px;
  padding: 0 15px;
  white-space: nowrap;
  min-width: 180px;
  color: var(--vt-c-white-soft);
  background-color: #2a08a5;
  border: 1px solid #cccccc;
  border-radius: $radius-sm;
  display: flex;
  align-items: center;
  @include font-weight-medium;
  justify-content: center;
  line-height: 1;
}

.business-name-update-button .btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background-color: rgba(#2a08a5, 0.7);
}

.business-name-alerts {
  margin: $spacing-3 0;
}

/* Fix vertical alignment of input boxes with labels */
.k-textbox {
  display: block;
  width: 100%;
  @include typography-body;
  height: 34px;
  line-height: normal;
  display: flex;
  align-items: center;
}

.k-input-inner {
  height: 100%;
  padding: 0.3rem 0.6rem;
  vertical-align: middle;
}

/* Ensure all inputs have consistent height and padding */
input.k-input-inner {
  height: 34px;
  box-sizing: border-box;
  vertical-align: middle;
  @include typography-body;
  display: flex;
  align-items: center;
}

:deep(.k-textbox) {
  @include typography-body;
  width: 100%;
  height: 34px;
}

.k-textbox.is-invalid {
  border-color: #e53935;
}

.step-validation-message {
  margin-top: 16px;
  padding: $spacing-2 $spacing-3;
  background-color: #fff3e0;
  @include typography-small;
  border-left: 4px solid #ff9800;
  border-radius: $radius-sm;
}

.validation-item {
  @include typography-small;
  margin-top: 4px;
  color: #e53935;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
}

/* Personal URL styling */
.personal-url-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.personal-url-display {
  padding: $spacing-2;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e0e0e0;
  border-radius: $radius-sm;
  @include typography-small;
  font-family: monospace;
  word-break: break-all;
}

.alert {
  @include typography-small;
  padding: $spacing-2-5 $spacing-4;
  margin-bottom: 12px;
  border-radius: $radius-sm;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert-dismissible {
  position: relative;
  padding-right: 36px;
}

.btn-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  @include typography-h4;
  @include font-weight-bold;
  border: 0;
  cursor: pointer;
  line-height: 1;
  opacity: 0.5;
}
</style>
